<template>
  <div :class="!isMobile ? 'container' : ''">
    <div class="docs-layout">
      <div v-if="showSideNav" class="side-nav">
        <Menu>
          <template #heading>
            <h1>{{ heading }}</h1>
          </template>
          <template #content>
            <div class="container">
              <ol v-if="navigation.length > 0">
                <li v-for="(category, index) in navigation" :key="index">
                  <button
                    class="doc-nav-link"
                    @click="handleSideBarLinkClick(category)"
                  >
                    {{ category.name }}
                    <i class="icon-arrow-right" />
                  </button>
                </li>
              </ol>
            </div>
          </template>
        </Menu>
      </div>

      <div v-if="showMainContent" class="main-content">
        <div v-if="!mq.mdPlus" class="menu__header">
          <div class="container">
            <span class="heading"> {{ selectedNav.name }} </span>
          </div>
          <div class="parent-route">
            <div class="container">
              <button class="back-button" @click="clearSelectedNav">
                <i class="icon-arrow-left" /> Back to {{ heading }}
              </button>
            </div>
          </div>
        </div>

        <div class="docs" :class="isMobile ? 'container' : ''">
          <Accordion
            v-for="(article, index) in content"
            :id="article.slug"
            :ref="article.slug"
            :key="index"
            :show-arrow="true"
            :disable-desktop="true"
            class="doc-item"
            :open-by-default="openAccordion(article)"
          >
            <template #header>
              <h2 class="article-heading">
                <component
                  :is="mq.mdPlus ? 'a' : 'span'"
                  :href="articleLink(index, article)"
                  class="article-heading__link"
                >
                  {{ article.index + 1 }}.{{ index + 1 }} - {{ article.name }}
                </component>
              </h2>
            </template>
            <template #body>
              <template v-if="htmlBased">
                <div class="wysiwyg" v-html="article.body" />
              </template>
              <template v-else>
                <template
                  v-for="(item, itemIndex) in article.content"
                  :key="itemIndex"
                >
                  <component :is="item.tag" v-if="item.tag" v-bind="item.props">
                    {{ item.text }}
                  </component>
                  <div v-else v-html="item.html"></div>
                </template>

                <template
                  v-if="
                    article.hasOwnProperty('children') &&
                    article.children.length > 0
                  "
                >
                  <div class="grid">
                    <div
                      v-for="(child, childIndex) in article.children"
                      :key="childIndex"
                      class="grid__item"
                    >
                      <h3>{{ childIndex + 1 }} : {{ child.name }}</h3>
                      <template
                        v-for="(childItem, childItemIndex) in child.content"
                        :key="childItemIndex"
                      >
                        <component
                          :is="childItem.tag"
                          v-if="childItem.tag"
                          v-bind="childItem.props"
                        >
                          {{ childItem.text }}
                        </component>
                        <div v-else v-html="childItem.html"></div>
                      </template>
                    </div>
                  </div>
                </template>
              </template>
            </template>
          </Accordion>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Accordion from "@/components/Accordion";
import Button from "@/components/Button.vue";
import DocBlock from "@/components/DocBlock";
import Menu from "@/components/Menu";
import ReturnsEstimator from "@/components/returns/ReturnsEstimator.vue";

export default {
  name: "DocsLayout",
  components: {
    Menu,
    Accordion,
    ReturnsEstimator,
    CustomButton: Button,
    DocBlock,
  },
  inject: ["mq"],
  props: {
    content: {
      type: Array,
      default: () => [],
    },
    navigation: {
      type: Array,
      default: () => [],
    },
    heading: {
      type: String,
      required: true,
    },
    htmlBased: {
      type: Boolean,
      default: false,
    },
    selectedNav: {
      type: Object,
      default: null,
    },
    selectedArticle: {
      type: String,
      default: null,
    },
  },
  emits: ["sideBarLinkClicked"],
  computed: {
    showSideNav() {
      if (this.mq.mdPlus) {
        return true;
      }
      return !this.selectedNav;
    },
    showMainContent() {
      if (this.mq.mdPlus) {
        return true;
      }

      return Boolean(this.selectedNav);
    },
    isMobile() {
      return !this.mq.mdPlus;
    },
  },
  watch: {
    content(val) {
      if (this.selectedArticle && val.length > 0) {
        this.$nextTick(() => this.scrollToArticle());
      }
    },
  },
  methods: {
    handleSideBarLinkClick(category) {
      this.$emit("sideBarLinkClicked", category);
    },
    articleLink(index, article) {
      return article.url;
    },
    clearSelectedNav() {
      this.$emit("sideBarLinkClicked", null);
    },
    scrollToArticle() {
      const itemElement = this.$refs[this.selectedArticle][0].$el;
      if (!itemElement) {
        return;
      }

      itemElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    },
    openAccordion(accordion) {
      if (this.content.length <= 1) {
        return true;
      }

      if (accordion.slug === this.selectedArticle) {
        return true;
      }

      return false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/components/wysiwyg";

.docs-layout {
  display: grid;
  grid-template-columns: auto;

  @include respond-above(md) {
    grid-template-columns: minmax(260px, 360px) 2fr;
  }

  ::v-deep(.button) {
    display: inline-block;
    margin-bottom: 40px;
  }
}

.doc-heading {
  background-color: $color-light;
  font-size: 16px;
  text-transform: uppercase;
  font-family: $heading-font-family;
  padding: 16px 20px;
}

.main-content {
  @include respond-above(md) {
    background-color: $color-light;
    padding: 60px 15%;
  }
}

.docs {
  padding-top: 20px;
}

.side-nav {
  & > * {
    height: 100vh;

    @include respond-above(md) {
      position: sticky;
      top: 0;
    }
  }
}

.doc-nav-link {
  width: 100%;
  text-align: left;
  display: flex;
  justify-content: space-between;
}

.doc-item {
  margin-bottom: 26px;

  @include respond-above(md) {
    margin-bottom: 60px;
  }

  ::v-deep() {
    p {
      font-size: 12px;
      margin-bottom: 20px;

      @include respond-above(md) {
        font-size: 14px;
      }
    }
  }

  ::v-deep(.accordion__header) {
    background: none;
    outline: none;
    border: none;
    width: 100%;
    padding: 6px 0;
    margin-bottom: 10px;
    text-align: left;

    h2 {
      font-family: $body-font-family;
      margin-bottom: 0;
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      padding: 0;

      @include respond-above(md) {
        font-size: 18px;
        margin-bottom: 20px;
        text-transform: none;
        font-weight: normal;
      }

      a {
        text-decoration: none;
      }
    }
  }
}

ol {
  counter-reset: item;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  margin-top: 20px;

  li {
    display: block;
    margin-bottom: 40px;
    position: relative;
    padding-left: 20px;

    a,
    button {
      text-decoration: none;
      outline: none;
      border: none;
      background: none;
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      padding: 0;
      margin: 0;
    }

    &::before {
      content: counters(item, ".") " - ";
      counter-increment: item;
      position: absolute;
      left: 0;
    }

    ol li {
      margin: 20px 0;
      padding-left: 40px;
    }
  }
}

.grid {
  @include respond-above(md) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 40px;
  }

  h3 {
    background-color: $color-light;
    padding: 10px 20px;
    font-weight: 600;
    font-family: $body-font-family;
    font-size: 12px;
    margin-bottom: 2px;

    @include respond-above(md) {
      background-color: $color-white;
    }
  }

  ::v-deep() {
    p {
      font-size: 12px;

      @include respond-above(md) {
        font-size: 14px;
      }
    }
  }
}

.menu {
  border: none;

  @include respond-above(md) {
    border: 1px solid rgba(7, 7, 7, 0.2);
  }

  .header {
    flex: 0 0 auto;
  }

  &__content {
    flex: 0 1 100%;
    padding-top: 10px;
    overflow-x: scroll;
  }

  .item {
    padding: 20px 0 30px;
  }

  &__bottom {
    flex: 0 0 auto;
    background-color: $color-light;
    border-top: 1px solid rgba($color-dark, 0.2);
    display: flex;
    padding: 0 10px;
    justify-content: space-evenly;
  }

  &__button {
    padding: 17px 0;
    flex: 0 0 auto;
    background: none;
    outline: none;
    border: none;
    font-size: 10px;
    opacity: 0.2;
    text-decoration: none;

    &.router-link-active {
      opacity: 1;
    }
  }

  &__header {
    flex: 0 0 auto;
    background: $color-light;
    padding-top: 16px;
    padding-bottom: 16px;

    .heading {
      font-size: 16px;
      text-transform: uppercase;
      font-family: $heading-font-family;
    }
  }
}

.parent-route {
  margin-top: 14px;
  background-color: $color-white;
}

.back-button {
  font-family: $body-font-family;
  font-size: 10px;
  display: block;
  width: 100%;
  text-decoration: none;
  padding: 14px 0;
  background: transparent;
  outline: none;
  border: none;
  text-align: left;

  .icon-arrow-left {
    margin-right: 10px;
  }
}

.wysiwyg {
  font-size: 12px !important;

  @include respond-above(md) {
    font-size: 14px !important;
  }

  p {
    font-size: 12px !important;

    @include respond-above(md) {
      font-size: 14px !important;
    }
  }
}
</style>
